/**
 * Notification history — pure helpers for the bell listener and the
 * paginated history view (useNotificationHistory).
 */

import type { NotificationItem } from '@/types/notification.types';

/** Visible items the history view aims to collect per "Load older" click. */
export const HISTORY_PAGE_SIZE = 25;

/**
 * Upper bound on Firestore fetches per click. Visibility filtering happens in
 * the browser, so a narrowly-configured user can get mostly-empty raw pages;
 * this stops one click from walking the whole collection.
 */
export const HISTORY_MAX_FETCHES_PER_LOAD = 4;

/** Build a NotificationItem from a raw Firestore document. */
export function parseNotificationDoc(id: string, data: Record<string, unknown>): NotificationItem {
  const timestamp = data.timestamp as { toMillis?: () => number } | null | undefined;
  return {
    id,
    type: data.type as NotificationItem['type'],
    locationId: data.locationId as string,
    title: data.title as string,
    body: data.body as string,
    targetRole: data.targetRole as NotificationItem['targetRole'],
    readBy: (data.readBy as string[] | undefined) || [],
    timestamp: timestamp?.toMillis?.() || Date.now(),
    metadata: data.metadata as NotificationItem['metadata'],
  };
}

/**
 * Build a personal NotificationItem from a `userNotifications/{email}/items` doc.
 * `ownerEmail` is the collection owner, used when the doc has no `recipient` field.
 */
export function parsePersonalNotificationDoc(
  id: string,
  data: Record<string, unknown>,
  ownerEmail: string
): NotificationItem {
  const timestamp = data.timestamp as { toMillis?: () => number } | null | undefined;
  const item: NotificationItem = {
    id,
    source: 'personal',
    type: data.type as NotificationItem['type'],
    locationId: '',
    title: (data.title as string) || '',
    body: (data.body as string) || '',
    targetRole: 'all',
    readBy: (data.readBy as string[] | undefined) || [],
    timestamp: timestamp?.toMillis?.() || Date.now(),
    recipient: ((data.recipient as string | undefined) || ownerEmail).toLowerCase(),
    metadata: data.metadata as NotificationItem['metadata'],
  };
  if (typeof data.url === 'string') item.url = data.url;
  return item;
}

/**
 * Whether one "Load older" click should fetch another raw page.
 * Stops once enough visible items are collected, the collection is exhausted
 * (last raw page came back short), or the fetch cap is reached.
 */
export function shouldFetchAnotherHistoryPage({
  visibleCollected,
  fetches,
  lastPageFull,
  target = HISTORY_PAGE_SIZE,
  maxFetches = HISTORY_MAX_FETCHES_PER_LOAD,
}: {
  visibleCollected: number;
  fetches: number;
  lastPageFull: boolean;
  target?: number;
  maxFetches?: number;
}): boolean {
  return lastPageFull && visibleCollected < target && fetches < maxFetches;
}

/** Append a page to the loaded history, dropping ids already present. */
export function mergeHistoryItems(existing: NotificationItem[], incoming: NotificationItem[]): NotificationItem[] {
  const seen = new Set(existing.map((n) => n.id));
  const merged = [...existing];
  for (const item of incoming) {
    if (seen.has(item.id)) continue;
    seen.add(item.id);
    merged.push(item);
  }
  return merged;
}

export interface NotificationDayGroup {
  key: string;
  label: string;
  items: NotificationItem[];
}

function localDayKey(ms: number): string {
  const d = new Date(ms);
  return `${d.getFullYear()}-${d.getMonth() + 1}-${d.getDate()}`;
}

/**
 * Group notifications (already newest-first) into local-calendar-day sections
 * labelled "Today", "Yesterday", or a full date.
 */
export function groupNotificationsByDay(items: NotificationItem[], now: number = Date.now()): NotificationDayGroup[] {
  const todayKey = localDayKey(now);
  const yesterday = new Date(now);
  yesterday.setDate(yesterday.getDate() - 1);
  const yesterdayKey = localDayKey(yesterday.getTime());

  const groups: NotificationDayGroup[] = [];
  for (const item of items) {
    const key = localDayKey(item.timestamp);
    let group = groups[groups.length - 1];
    if (!group || group.key !== key) {
      const label =
        key === todayKey
          ? 'Today'
          : key === yesterdayKey
            ? 'Yesterday'
            : new Date(item.timestamp).toLocaleDateString([], {
                weekday: 'short',
                month: 'short',
                day: 'numeric',
                year: 'numeric',
              });
      group = { key, label, items: [] };
      groups.push(group);
    }
    group.items.push(item);
  }
  return groups;
}
